import {
  Button,
  ButtonGroup,
  DataStatus,
  Form,
  FormButton,
  FormSubmitHandler,
  FormField,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {head, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {useGetEmployeeMechanicsQuery} from '@dms/api/customer';
import {
  useGetMetadaServiceCaseQuery,
  useGetServiceOrderJobQuery,
  usePostServiceItemsMaterialPartIssuedMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError, usePermissions} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

type BulkAssignMechanicFormType = {
  assignMechanicId?: string | Nullish;
};

interface IssueMaterialItemsProps extends RequiredTestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  items: {serviceJobId: string; serviceItemId: string}[];
  defaultMechanicId?: string | Nullish;
  onSubmit: VoidFunction;
  onClose: VoidFunction;
}

export function IssueMaterialItems(props: IssueMaterialItemsProps) {
  const [canReadEmployee] = usePermissions({permissionKeys: ['employeeRead']});

  const {data, isLoading, isError} = useGetServiceOrderJobQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
    serviceJobId: head(props.items)?.serviceJobId ?? '',
  });
  const {data: serviceCase} = useGetMetadaServiceCaseQuery(
    {serviceCaseId: props.serviceCaseId},
    {skip: isNil(props.serviceCaseId)}
  );
  const {data: mechanics, isLoading: isMechanicOptionsLoading} = useGetEmployeeMechanicsQuery(
    {
      authorizationProfileId: serviceCase?.authorizationProfileId ?? '',
      isActive: true,
      withToOffer: true,
    },
    {
      skip: isNil(serviceCase?.authorizationProfileId) || !canReadEmployee,
    }
  );

  const mechanicOptions =
    mechanics?.map((mechanic) => ({
      label: mechanic?.name,
      value: mechanic?.employeeId,
    })) ?? [];

  const [postServiceItemsMaterialPartIssued] = usePostServiceItemsMaterialPartIssuedMutation();

  const handleSubmit: FormSubmitHandler<BulkAssignMechanicFormType> = async (data) => {
    await postServiceItemsMaterialPartIssued({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      body: {
        serviceItems: props.items,
        assignMechanic: {
          id: data.assignMechanicId ?? '',
          isDefault: true,
          costCenterId:
            mechanics?.find((mechanic) => mechanic?.employeeId === data.assignMechanicId)
              ?.costCenterId ?? null,
        },
      },
    })
      .unwrap()
      .then(() => {
        props.onSubmit();
        props.onClose();
      })
      .catch(handleApiError);
  };

  const defaultValues: BulkAssignMechanicFormType = {
    assignMechanicId:
      props.defaultMechanicId ?? head(data?.assignMechanics?.filter(isNotNil) ?? [])?.id,
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={34}>
      <Form<BulkAssignMechanicFormType>
        experimentalZodSchema={fromSchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              label={i18n.t('entity.order.labels.mechanic')}
              name="assignMechanicId"
              type="choice"
              options={mechanicOptions}
              isLoading={isMechanicOptionsLoading}
              data-testid={suffixTestId('mechanic', props)}
              menuInPortal
            />
            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discard')}
                variant="secondary"
                onClick={props.onClose}
                data-testid={suffixTestId('discardButton', props)}
              />
              <FormButton
                control={control}
                type="submit"
                title={i18n.t('general.actions.confirm')}
                data-testid={suffixTestId('confirmButton', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}

const fromSchema = z.object({
  assignMechanicId: z.string(),
});
